import * as React from 'react';
import { useState } from 'react';
import { User, WorkArea } from '../../../services/permissionService';
import styles from './AdminOverview.module.css';

interface ChangeWorkAreaModalProps {
  user: User;
  availableWorkAreas: WorkArea[];
  onUpdate: (hasUnrestrictedAccess: boolean, workAreaIds: number[]) => Promise<void>;
  onClose: () => void;
  loading: boolean;
}

export function ChangeWorkAreaModal({ user, availableWorkAreas, onUpdate, onClose, loading }: ChangeWorkAreaModalProps): React.JSX.Element {
  const [hasUnrestrictedAccess, setHasUnrestrictedAccess] = useState<boolean>(
    user.has_unrestricted_work_area_access
  );
  const [selectedWorkAreaIds, setSelectedWorkAreaIds] = useState<number[]>(
    user.work_area_restrictions.map(wa => wa.id)
  );

  function handleWorkAreaToggle(workAreaId: number): void {
    setSelectedWorkAreaIds(prev =>
      prev.includes(workAreaId)
        ? prev.filter(id => id !== workAreaId)
        : [...prev, workAreaId]
    );
  }

  function handleUnrestrictedAccessChange(hasAccess: boolean): void {
    setHasUnrestrictedAccess(hasAccess);
    if (hasAccess) {
      setSelectedWorkAreaIds([]);
    }
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        <h2 className={styles.modalTitle}>
          Change Work Area Access for {user.first_name} {user.last_name}
        </h2>

        <div className={styles.modalSection}>
          <h3 className={styles.modalSectionTitle}>Access Type</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label className={`${styles.radioItem} ${hasUnrestrictedAccess ? styles.checkboxItemSelectedGreen : ''}`}>
              <input
                type="radio"
                checked={hasUnrestrictedAccess}
                onChange={() => handleUnrestrictedAccessChange(true)}
                style={{ margin: 0 }}
              />
              <span>Unrestricted access to all work areas</span>
            </label>
            <label className={`${styles.radioItem} ${!hasUnrestrictedAccess ? styles.checkboxItemSelectedAlt : ''}`}>
              <input
                type="radio"
                checked={!hasUnrestrictedAccess}
                onChange={() => handleUnrestrictedAccessChange(false)}
                style={{ margin: 0 }}
              />
              <span>Restricted to specific work areas</span>
            </label>
          </div>
        </div>

        {!hasUnrestrictedAccess && (
          <div className={styles.modalSection}>
            <h3 className={styles.modalSectionTitle}>Select Work Areas</h3>
            <div className={styles.checkboxListCompact}>
              {availableWorkAreas.map(workArea => (
                <label
                  key={workArea.id}
                  className={`${styles.checkboxItem} ${selectedWorkAreaIds.includes(workArea.id) ? styles.checkboxItemSelectedAlt : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedWorkAreaIds.includes(workArea.id)}
                    onChange={() => handleWorkAreaToggle(workArea.id)}
                    style={{ margin: 0 }}
                  />
                  <span>{workArea.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className={styles.buttonRow}>
          <button onClick={onClose} disabled={loading} className={styles.cancelButton}>
            Cancel
          </button>
          <button
            onClick={() => onUpdate(hasUnrestrictedAccess, selectedWorkAreaIds)}
            disabled={loading}
            className={styles.submitButton}
          >
            {loading ? 'Updating...' : 'Update Work Area Access'}
          </button>
        </div>

        <button onClick={onClose} className={styles.modalClose} aria-label="Close">
          &times;
        </button>
      </div>
    </div>
  );
}
